import { FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";

import { ProviderNetworkType } from "@app/projects/enumerators";
import { AdequacyDetail } from "@app/projects/network-assessment/adequacy";
import ProjectService from "@app/projects/projectService";
import { ServicingProvider } from "@app/projects/results/servicing-provider";
import { QESAlert, QESButton } from "@app/shared/qes-standards";
import { AccordionContainer, Accordion, LoadingSpinner } from "@app/shared";
import { ColumnConfig, ColumnGroup, Waffles } from "@app/shared/waffles";
import { CountyProjectContext } from "@app/projects";
import { getServicingGranularityHeader } from "@app/projects/network-assessment/adequacy/utility";
import { Roles, UserInfo } from "@app/user";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { PreviewWaffleToppings, simulationPreviewWaffleToppings } from "../SimulationPreviewHelpers";

interface SimulateProviderTerminationProps {
  providerNetworkType: ProviderNetworkType;
  regionSetId: number;
  selectedProviders: ServicingProvider[];
  specialtyGroupCode: string;
  user: UserInfo;
  identifier: string;
  allLocations: boolean;
  allSpecialties: boolean;
}

const SimulateProviderTermination: FC<React.PropsWithChildren<SimulateProviderTerminationProps>> = (props: SimulateProviderTerminationProps) => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<AdequacyDetail[]>([]);
  const projectService = useRef<ProjectService>(new ProjectService()).current;
  const [error, setError] = useState<JSX.Element>(null);
  const projectContext = useContext(CountyProjectContext);
  const { adequacyResult, cmsCreditIsApplying, runConfiguration, networkAssessmentAnalysisType, project, templateMetadata } = projectContext;
  const adequacyData = adequacyResult.details;
  const { projectRunConfigurationType } = runConfiguration;
  const [previouslyTerminatedProvidersCount, setPreviouslyTerminatedProvidersCount] = useState<number>(null);
  const [controlsChanged, setControlsChanged] = useState<boolean>(false)
  const [allSpecialties, setAllSpecialties] = useState<boolean>(props.allSpecialties);
  const [allLocations, setAllLocations] = useState(props.allLocations);
  const [identifier, setIdentifier] = useState(props.identifier);
  const tinViewer = props.user.roles.includes(Roles.TIN_VIEWER);


  useEffect(() => {
    //Before simulating, validate that all providers have NPI or TIN as appropriate, based on TIN_Viewer role
    validateProviderTypes(props.selectedProviders, tinViewer);

    projectService.simulateTermination(
      project.projectId,
      props.regionSetId,
      {
        allLocations: false,
        allSpecialties: false,
        providerIds: props.selectedProviders.map((sp) => sp.providerId),
        providerNetworkType: props.providerNetworkType,
        terminateByNPI: identifier.includes("npi"),
        terminateByTIN: identifier.includes("tin") && tinViewer
      },
      props.specialtyGroupCode,
      projectRunConfigurationType,
      networkAssessmentAnalysisType
    ).then((result) => {
      setPreview([result]);
      setPreviouslyTerminatedProvidersCount(result.previouslyTerminatedProvidersCount);
      setLoading(false);
      setControlsChanged(false);
    }, () => {
      setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
    });
  }, []);

  useEffect(() => {
    if (props.allLocations != allLocations || props.allSpecialties != allSpecialties || props.identifier != identifier)
      setControlsChanged(true);

    setAllLocations(props.allLocations);
    setAllSpecialties(props.allSpecialties);
    setIdentifier(props.identifier);

  }, [props.allLocations, props.allSpecialties, props.identifier]);

  const terminationPreviewWaffleToppings: PreviewWaffleToppings[] = useMemo(() => {
    return simulationPreviewWaffleToppings(preview, adequacyData, cmsCreditIsApplying);
  }, [preview, adequacyData]);

  const validateProviderTypes = (selectedProviders: ServicingProvider[], hasTINViewerRole: boolean): void => {
    let hasNPIOnly = false;
    let hasTINOnly = false;
    let allHaveNPI = true;

    for (let i = 0; i < selectedProviders.length; i++) {
      if (selectedProviders[i].npi === undefined) {
        allHaveNPI = false;

        if (selectedProviders[i].tin !== undefined) {
          hasTINOnly = true;
        }
      } else if (selectedProviders[i].npi !== undefined && selectedProviders[i].tin === undefined) {
        hasNPIOnly = true;
      }
    }

    if (hasTINViewerRole && hasNPIOnly && hasTINOnly) {
      // Throw an error if there are providers with only NPIs and providers with only TINs.
      setError(<QESAlert type="danger">All selected providers must have NPIs or all selected providers must have TINs.</QESAlert>);
    } else if (!hasTINViewerRole && !allHaveNPI) {
      // Throw an error if there are providers with only NPIs and providers with only TINs.
      setError(<QESAlert type="danger">All selected providers must have NPIs.</QESAlert>);
    }
  };

  const columnConfig: ColumnConfig = useMemo(() => {
    const showWithTimeAndDistanceColumns = runConfiguration.showWithTimeAndDistanceColumns;

    return {
      accessMet: {
        displayText: "Met",
        hidden: showWithTimeAndDistanceColumns,
        noSort: true,
        testId: "access-met",
      },
      accessMetDist: {
        displayText: "Met Dist",
        hidden: !showWithTimeAndDistanceColumns,
        noSort: true,
        testId: "access-met-dist",
      },
      accessMetTime: {
        displayText: "Met Time",
        hidden: !showWithTimeAndDistanceColumns,
        noSort: true,
        testId: "access-met-time",
      },
      accessWith: {
        displayText: "With %",
        noSort: true,
        testId: "access-with-percent",
      },
      accessWithout: {
        displayText: "Without %",
        noSort: true,
        testId: "access-without-percent",
      },
      overallGapAge: {
        displayText: "Gap Age",
        hidden: !project.includeGapAging,
        noSort: true,
        testId: "overall-gap-age",
      },
      overallMet: {
        displayText: "Met",
        noSort: true,
        testId: "overall-met",
      },
      servicingDistinct: {
        displayText: "Servicing",
        noSort: true,
        testId: "servicing-distinct",
      },
      servicingMet: {
        displayText: "Met",
        noSort: true,
        testId: "servicing-met",
      },
      servicingProviderCount: {
        displayText: getServicingGranularityHeader(runConfiguration.template.servicingAnalysisGranularity),
        noSort: true,
        testId: "servicing-provider-count",
      },
      servicingRequired: {
        displayText: "Required",
        noSort: true,
        testId: "servicing-required",
      },
    };
  }, [runConfiguration, project, templateMetadata]);

  const columnGroups: Array<ColumnGroup<PreviewWaffleToppings>> = [
    {
      children: ["accessWith", "accessWithout", "accessMetDist", "accessMetTime", "accessMet"],
      hideKey: false,
      key: "Access Requirements",
      testId: "group-access-requirements",
    },
    {
      children: ["servicingRequired", "servicingProviderCount", "servicingDistinct", "servicingMet"],
      key: "Servicing Provider Requirements",
      testId: "group-servicing-requirements",
    },
    {
      children: ["overallMet", "overallGapAge"],
      key: "Overall",
      testId: "group-overall",
    },
  ];

  const rowDecorator = (record: PreviewWaffleToppings) => {
    if (record.overallMet === "Y") {
      return `success`;
    } else if (record.accessMet === "Y" || record.servicingMet === "Y") {
      return `warning`;
    } else {
      return `danger`;
    }
  };

  const handleReloadRequest = () => {
    setLoading(true);
    setControlsChanged(false);
    setPreview([]);

    projectService.simulateTermination(
      project.projectId,
      props.regionSetId,
      {
        allLocations: allLocations,
        allSpecialties: allSpecialties,
        providerIds: props.selectedProviders.map((sp) => sp.providerId),
        providerNetworkType: props.providerNetworkType,
        terminateByNPI: identifier.includes("npi"),
        terminateByTIN: identifier.includes("tin") && tinViewer
      },
      props.specialtyGroupCode,
      projectRunConfigurationType,
      networkAssessmentAnalysisType
    ).then((result) => {
      setPreview([result]);
      setPreviouslyTerminatedProvidersCount(result.previouslyTerminatedProvidersCount);
      setLoading(false);
    }, () => {
      setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
    });

  }

  const renderLoadedContent = (selectedProviders: ServicingProvider[]) => {
    return (
      <>
        <div className="mb-3">
          <p className="mb-0">The top row is what this county/specialty combination looks like without termination.</p>
          {previouslyTerminatedProvidersCount === 0 &&
          <p>The bottom row is what it looks like with the terminated providers listed below.</p>}
          {previouslyTerminatedProvidersCount > 0 &&
          <QESAlert type="info" persist={true}>
            <div>
              The bottom row is what your adequacy results would look like if the terminated provider(s) listed below and {previouslyTerminatedProvidersCount} other provider(s) who have been terminated previously were removed from your network. For full visibility for terminated providers, we recommend running the following report:
              <ul>
                <li>Network Terminated Provider Report</li>
              </ul>
            </div>
          </QESAlert>}
          {renderTable(terminationPreviewWaffleToppings)}
          <div className= "mt-3">
            <QESButton qesType="secondary" qesTestId="simulate-terminations__reload_simulation" onClick={handleReloadRequest} disabled={!controlsChanged}>Rerun Termination Preview</QESButton>
          </div>
        </div>
        <div className="mt-3 mb-5">
          <AccordionContainer>
            <Accordion
              title={<>{<QesIcon isDecorative iconName={QesIcons.FontAwesome.UserMd} width="11" height="14" classNames="align-baseline mr-1" svgStyle={{ position: "relative", top: "1px" }} />}Selected Providers</>}
              defaultOpen={true}
              testId="simulate-provider-termination__selected-providers-accordion"
            >
              <ul>
              {selectedProviders.map((provider) => <li key={provider.providerId}>
                {provider.name} | {provider.npi ? provider.npi + " | " : ""}{provider.address} {provider.city}, {provider.state} {provider.zip}
              </li>)}
              </ul>
            </Accordion>
          </AccordionContainer>
        </div>
      </>
    );
  };

  const renderTable = (tableData: PreviewWaffleToppings[]) => {
    return (
      <Waffles<PreviewWaffleToppings>
        columns={columnConfig}
        columnGroups={columnGroups}
        rowDecorator={rowDecorator}
        loading={loading}
        data={tableData}
        testId="simulate-provider-termination"
      />
    );
  };

  return (
    <div>
      <div className="row">
        <div className="col-12">
          {error ? error :
            !!preview ? renderLoadedContent(props.selectedProviders) : <LoadingSpinner />}
        </div>
      </div>
    </div>
  );
};

export { SimulateProviderTermination };
